import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ExternalLink, BookOpen, Calendar, Users, Award } from 'lucide-react'
import { getJournal, getProjects } from '../../services/api'
import { stripJatsXml } from '../../utils/stripJatsXml'
import { formatTopicLabel } from '../../utils/topics'
import { parseStructuredAbstract } from '../../utils/parseStructuredAbstract'
import InlineConnectionsPanel from '../../components/graph/InlineConnectionsPanel'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import MathText from '../../components/shared/MathText'
import AttachToProjectMenu from '../../components/journals/AttachToProjectMenu'
import AddToListMenu from '../../components/lists/AddToListMenu'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'

export default function JournalDetail() {
  const params = useParams()
  // Journal IDs are DOIs with slashes (e.g. 10.1007/s10796-021-10199-5)
  // so we use a wildcard route and extract the full path via '*'
  // Links encode the id (an openalex id carries a whole URL with '//'), so decode it back; a plain
  // id with no '%' passes through unchanged.
  const rawJournalId = params['*']
  let journalId = rawJournalId
  try { journalId = rawJournalId ? decodeURIComponent(rawJournalId) : rawJournalId } catch { journalId = rawJournalId }
  const navigate = useNavigate()

  const { data: journal, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['journal', journalId],
    queryFn: () => getJournal(journalId!),
    enabled: !!journalId,
  })

  // Powers the attach-to-project menu in the header (same call the browse cards use).
  const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: getProjects })

  if (isLoading) return <LoadingSkeleton rows={6} type="list" />

  // UX-032: only a real 404 means the paper does not exist. Any other failure
  // (500, timeout, offline) is a temporary problem and must offer a retry.
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  const isNotFound = !error || status === 404
  if (error || !journal) {
    return (
      <div className="max-w-4xl mx-auto">
        <EmptyState
          icon={BookOpen}
          title={isNotFound ? 'We couldn\u2019t find this paper' : 'This paper couldn\u2019t be loaded'}
          description={
            isNotFound
              ? 'It may have been removed, or the link may be incorrect. You can search for it from Journals.'
              : 'Something went wrong on our side. Please try again in a moment.'
          }
          action={
            <div className="flex items-center gap-2">
              {!isNotFound && (
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-60"
                >
                  Retry
                </button>
              )}
              <button
                onClick={() => navigate('/journals')}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg ${
                  isNotFound ? 'text-white bg-iflow-700 hover:bg-iflow-800' : 'text-iflow-700 border border-sky-200 hover:bg-white/50'
                }`}
              >
                <ArrowLeft size={14} /> Back to Journals
              </button>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back nav */}
      <button
        onClick={() => navigate('/journals')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={14} /> Back to Journals
      </button>

      <PageHeader
        title={journal.title}
        helpKey="journals"
        primaryAction={
          <div className="flex items-center gap-2">
            <AttachToProjectMenu projects={projects} journalId={journal._id} journalTitle={journal.title} />
            <AddToListMenu resourceId={journal._id} resourceType="journal" title={journal.title} variant="button" />
          </div>
        }
      />

      {/* Metadata card */}
      <div className="light-card p-6 mb-6">
        {/* Authors */}
        {journal.authors && journal.authors.length > 0 && (
          <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <Users size={14} className="text-gray-500 flex-shrink-0" />
            <span>{journal.authors.join(', ')}</span>
          </div>
        )}

        {/* Publication info row */}
        <div className="flex flex-wrap items-center gap-4 mt-3">
          {journal.journal_name && (
            <span className="text-sm text-gray-600 italic">{journal.journal_name}</span>
          )}
          {journal.publication_date && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Calendar size={13} />
              {journal.publication_date}
            </span>
          )}
          {journal.citation_count != null && journal.citation_count > 0 && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Award size={13} />
              {journal.citation_count.toLocaleString()} citations
            </span>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {journal.peer_reviewed && (
            <span className="px-2.5 py-1 text-xs font-medium bg-green-500/20 text-green-700 rounded-full">
              Peer Reviewed
            </span>
          )}
          {journal.open_access && (
            <span className="px-2.5 py-1 text-xs font-medium bg-iflow-50 text-iflow-700 rounded-full">
              Open Access
            </span>
          )}
          {journal.resource_type && (
            <span className="px-2.5 py-1 text-xs font-medium bg-sky-500/10 text-gray-600 rounded-full">
              {journal.resource_type}
            </span>
          )}
        </div>

        {/* DOI */}
        {journal.doi && (
          <a
            href={`https://doi.org/${journal.doi}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm text-iflow-600 hover:text-iflow-700"
          >
            <ExternalLink size={13} />
            DOI: {journal.doi}
          </a>
        )}

        {/* Abstract */}
        {journal.abstract && (() => {
          const parsed = parseStructuredAbstract(stripJatsXml(journal.abstract))
          const isStructured = parsed.sections.length > 1 || parsed.sections[0]?.heading !== ''
          return (
            <div className="mt-4 pt-4 border-t border-sky-200/50">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Abstract</h3>
              {isStructured ? (
                <div className="space-y-2">
                  {parsed.sections.map((sec, i) => (
                    <div key={i}>
                      {sec.heading && (
                        <span className="text-xs font-semibold uppercase tracking-wide text-iflow-600 mr-1.5">
                          {sec.heading}
                        </span>
                      )}
                      <span className="text-sm text-gray-600 leading-relaxed">
                        {parsed.hasMath ? <MathText>{sec.content}</MathText> : sec.content}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600 leading-relaxed">
                  {parsed.hasMath
                    ? <MathText>{parsed.sections[0]?.content ?? ''}</MathText>
                    : parsed.sections[0]?.content}
                </p>
              )}
            </div>
          )
        })()}

        {/* Bug #6685: say so when the source record carries no abstract, rather than showing nothing */}
        {!journal.abstract && (
          <div className="mt-4 pt-4 border-t border-sky-200/50">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Abstract</h3>
            <p className="text-sm text-gray-500">The source did not supply an abstract for this article.</p>
          </div>
        )}

        {/* Topics */}
        {journal.topics && journal.topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-sky-200/50">
            {journal.topics.slice(0, 8).map((t) => (
              <span key={t} className="px-2 py-0.5 bg-sky-500/10 text-gray-600 text-xs rounded-full">
                {formatTopicLabel(t)}
              </span>
            ))}
            {journal.topics.length > 8 && (
              <span className="text-[10px] text-gray-500">+{journal.topics.length - 8} more</span>
            )}
          </div>
        )}
      </div>

      {/* Connections panel */}
      <InlineConnectionsPanel resourceId={journal._id} title="Journal Connections" />
    </div>
  )
}
